import _ from 'underscore';
import { ProcessOptions, SelectOption, SortOrder, TreeNode } from '@app/interfaces';
import { toStartCase } from './transforms';

export const mapArrayToSelectOptions = <T>(
	data: T[],
	opts: {
		value: keyof T;
		label?: keyof T | ((value: T) => string);
		order?: SortOrder;
		sort?: 'value' | 'label' | keyof T;
		all?: boolean;
		allLabel?: string;
		allValue?: any;
		valueToString?: boolean;
		skipUniqValues?: boolean;
	}
): SelectOption[] => {
	const defaultOpts: any = { key: 'Id', order: 'asc', sort: 'label', all: true, allLabel: 'All', valueToString: false };

	if (!opts.label) {
		defaultOpts.label = opts.value;
	}

	const options = { ...defaultOpts, ...opts };

	const { value, label, order, sort, all, allLabel, allValue } = options;

	const sortedData = [...data];
	const sortByKeyOf = sort !== 'value' && sort !== 'label';
	if (sortByKeyOf) {
		sortedData.sort((a, b) => String(a[sort]).localeCompare(String(b[sort]), undefined, { numeric: typeof a[sort] === 'number' }));
	}

	let selectOptions = sortedData
		.filter((x) => x[value] !== undefined && x[value] !== null && String(x[value]).trim())
		.map((x) => ({ label: String(typeof options.label === 'function' ? options.label(x) : x[label]).trim(), value: x[value] } as SelectOption));

	if (!opts.skipUniqValues) {
		selectOptions = _.uniq(selectOptions, (x) => x.label);
	}
	if (!sortByKeyOf) {
		selectOptions.sort((a, b) => String(a[sort]).localeCompare(String(b[sort]), undefined, { numeric: typeof a[sort] === 'number' }));
	}

	if (order === 'desc') {
		selectOptions.reverse();
	}

	if (options.valueToString) {
		selectOptions.forEach((x) => (x.value = x.value?.toString()));
	}

	if (all) {
		selectOptions.unshift({
			label: allLabel,
			value: allValue,
		});
	}

	return selectOptions;
};

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export const mapEnumToSelectOptions = (value: any, map?: { [key: string]: string }, includeAll = true, allLabel = 'All'): SelectOption[] => {
	// defaults to "Start Case" of the enum key as the label
	const data = Object.keys(value)
		.filter((key) => !isNaN(+key))
		.map((key) => ({ key: Number(key), label: map && map[key] ? map[key] : toStartCase(String(value[key])) }));

	return mapArrayToSelectOptions<any>(data, { value: 'key', label: 'label', sort: 'value', all: includeAll, allLabel });
};

export const mapArrayToTreeOptions = <T, V>(data: T[], options: ProcessOptions<T>): TreeNode<V>[] => {
	const { groupKey, nameKey, valueKey, order, sort, filter } = options;
	if (filter) {
		data = data.filter((item) => item[filter]);
	}
	const groupedData = data.reduce((acc, item) => {
		const groupName = item[groupKey] as unknown as string;
		if (!acc.has(groupName)) {
			acc.set(groupName, []);
		}
		const name = typeof nameKey === 'function' ? nameKey(item) : (item[nameKey] as unknown as string);
		const node: TreeNode<V> = {
			name,
			value: item[valueKey] as unknown as V,
		};

		acc.get(groupName).push(node);

		return acc;
	}, new Map<string, TreeNode<V>[]>());
	if (sort) {
		const defaultOrder = order || 'asc';
		groupedData.forEach((nodes) => {
			nodes.sort((a, b) => {
				const aItem = data.find((item) => String(item[valueKey]) === String(a.value));
				const bItem = data.find((item) => String(item[valueKey]) === String(b.value));
				if (aItem && bItem) {
					const aSortValue = aItem[sort];
					const bSortValue = bItem[sort];
					if (aSortValue < bSortValue) {
						return defaultOrder === 'asc' ? -1 : 1;
					}
					if (aSortValue > bSortValue) {
						return defaultOrder === 'asc' ? 1 : -1;
					}
					return 0;
				}
				return 0;
			});
		});
	}
	return Array.from(groupedData.entries())
		.map(([groupName, nodes]) => ({
			name: groupName,
			value: `All ${groupName}` as unknown as V,
			children: nodes,
		}))
		.sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));
};
